"use client";

import Link from "next/link";
import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { Bot, MessageSquare, Shield } from "lucide-react";
import { TbCornerLeftUpDouble } from "react-icons/tb";
import { loginAction } from "@/app/actions/auth.actions";
import { Alert } from "@/components/ui/Alert";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import type { LoginState } from "@/types/auth";

function SubmitButton() {
    const { pending } = useFormStatus();

    return (
        <Button className="h-11 w-full rounded-lg text-base" type="submit" disabled={pending}>
            {pending ? (
                <>
                    <Spinner />
                    Entrando…
                </>
            ) : (
                "Iniciar sesión"
            )}
        </Button>
    );
}

const FEATURES = [
    {
        icon: MessageSquare,
        title: "WhatsApp y canales unificados",
        description: "Gestiona conversaciones desde un solo panel.",
    },
    {
        icon: Bot,
        title: "IA conversacional",
        description: "Automatiza respuestas con skills configurables.",
    },
    {
        icon: Shield,
        title: "Multi-tenant seguro",
        description: "Aislamiento por empresa y control de acceso.",
    },
] as const;

export function LoginClient() {
    const [state, formAction] = useActionState<LoginState, FormData>(loginAction, {
        ok: false,
    });

    return (
        <div className="min-h-screen lg:grid lg:grid-cols-2">
            {/* Panel de marca */}
            <div className="relative hidden overflow-hidden bg-foreground text-background dark:bg-sidebar-background dark:text-foreground lg:flex lg:flex-col lg:justify-between">
                <div
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_20%_0%,hsl(var(--primary)/0.4),transparent),radial-gradient(ellipse_60%_50%_at_80%_100%,hsl(var(--primary)/0.25),transparent)]"
                    aria-hidden
                />
                <div
                    className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(hsl(var(--background)/0.6)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--background)/0.6)_1px,transparent_1px)] [background-size:48px_48px] dark:[background-image:linear-gradient(hsl(var(--foreground)/0.6)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--foreground)/0.6)_1px,transparent_1px)]"
                    aria-hidden
                />

                <div className="relative z-10 p-10 xl:p-14">
                    <div className="flex items-center gap-3">
                        <TbCornerLeftUpDouble className="size-8 text-primary" aria-hidden />
                        <span className="text-xl font-semibold tracking-tight">Automatari</span>
                    </div>
                </div>

                <div className="relative z-10 flex flex-1 flex-col justify-center px-10 py-8 xl:px-14">
                    <p className="mb-3 text-sm font-medium uppercase tracking-widest text-primary">
                        Plataforma conversacional
                    </p>
                    <p className="max-w-md text-4xl font-bold leading-tight tracking-tight xl:text-5xl">
                        Automatiza conversaciones con inteligencia
                    </p>
                    <p className="mt-5 max-w-md text-base leading-relaxed text-background/70 dark:text-foreground/70">
                        Accede a tu consola para operar canales, skills de IA y flujos de atención
                        en tiempo real.
                    </p>

                    <ul className="mt-10 space-y-5">
                        {FEATURES.map(({ icon: Icon, title, description }) => (
                            <li key={title} className="flex gap-4">
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-background/10 ring-1 ring-background/10 dark:bg-foreground/10 dark:ring-foreground/10">
                                    <Icon className="size-5 text-primary" />
                                </div>
                                <div>
                                    <p className="font-medium">{title}</p>
                                    <p className="mt-0.5 text-sm text-background/60 dark:text-foreground/60">{description}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className="relative z-10 p-10 text-sm text-background/50 dark:text-foreground/50 xl:p-14">
                    © {new Date().getFullYear()} Automatari
                </p>
            </div>

            {/* Formulario */}
            <div className="relative flex min-h-screen flex-col justify-center bg-background px-6 py-12 sm:px-10">
                <div
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.08),transparent_50%)]"
                    aria-hidden
                />
                <ThemeToggle className="absolute right-4 top-4 z-10 sm:right-6 sm:top-6" />

                <div className="relative mx-auto w-full max-w-lg">
                    <div className="mb-8 flex flex-col items-center text-center">
                        <div className="mb-6 flex items-center justify-center gap-3 lg:mb-8">
                            <TbCornerLeftUpDouble className="size-10 text-primary" aria-hidden />
                            <span className="text-xl font-semibold tracking-tight">Automatari</span>
                        </div>
                        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                            Bienvenido de nuevo
                        </h1>
                        <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                            Ingresa tus credenciales para acceder al panel.
                        </p>
                    </div>

                    <div className="rounded-2xl border border-border/60 bg-card/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                        <form className="space-y-5" action={formAction}>
                            {state.message && (
                                <Alert
                                    variant={state.ok ? "success" : "destructive"}
                                    role="status"
                                    aria-live="polite"
                                    className="grid-cols-1"
                                >
                                    {state.message}
                                </Alert>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="email">Correo electrónico</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    placeholder="nombre@empresa.com"
                                    required
                                    className="h-11 rounded-lg bg-background/50"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">Contraseña</Label>
                                    <Link
                                        href="/forgot-password"
                                        className="text-xs text-muted-foreground transition-colors hover:text-primary"
                                    >
                                        ¿Olvidaste tu contraseña?
                                    </Link>
                                </div>
                                <Input
                                    id="password"
                                    type="password"
                                    name="password"
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    required
                                    className="h-11 rounded-lg bg-background/50"
                                />
                            </div>

                            <SubmitButton />
                        </form>
                    </div>

                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        ¿No tienes una cuenta?{" "}
                        <Link
                            href="/register"
                            className="font-medium text-primary underline-offset-4 hover:underline"
                        >
                            Crear cuenta
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
